import { data_from_server_post, get_base_url } from '@/shared/http/client';

/**
 * Sentinel accepted by `versions-diff` as `to_distillation_event_id`: diffs a
 * version's train snapshot against the CURRENT live datasets (task page's
 * "Versions diff" entry while a retrain is pending).
 */
export const CURRENT_VERSION_EVENT_ID = 'current';

/**
 * Paginated record-level train-set diff between two distillation events.
 * Response: { records, total, page, page_size, summary, distribution, train_sets }.
 */
export async function fetchVersionsDiff({
  client_id,
  llm_usecase_id,
  from_distillation_event_id,
  to_distillation_event_id,
  page = 1,
  page_size = 10,
}) {
  return await data_from_server_post('versions-diff', {
    client_id,
    llm_usecase_id,
    from_distillation_event_id,
    to_distillation_event_id,
    page,
    page_size,
  });
}

/**
 * Download the merged train-set snapshot of one version as a JSON file.
 * Mirrors the `download-benchmark-version` blob pattern
 * (see modelDrawerDownloadBenchmark).
 */
export async function downloadTrainSetVersion({ client_id, distillation_event_id, fileName }) {
  const url = `${get_base_url(window.location.origin)}/download-train-set-version`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id,
      distillation_event_id,
      format: 'json',
    }),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(errorData?.detail || errorData?.message || 'Failed to download train set');
  }

  const blob = await response.blob();
  saveBlob(blob, fileName || 'train_set.json');
}

/**
 * Download the CURRENT live train datasets of a task as a JSON file
 * (`download-dataset`) — the "to" side of a snapshot-vs-current diff.
 */
export async function downloadCurrentTrainSet({ client_id, llm_usecase_id, task_type, fileName }) {
  const url = `${get_base_url(window.location.origin)}/download-dataset`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id,
      llm_usecase_id,
      task_type,
      is_benchmark: false,
      format: 'json',
    }),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(errorData?.detail || errorData?.message || 'Failed to download train set');
  }

  const blob = await response.blob();
  saveBlob(blob, fileName || 'train_set_current.json');
}

function saveBlob(blob, fileName) {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}
